// ✅ ENHANCED MediaPreview.jsx — Veo3-Style Glassmorphism & Glow
import React, { useState, useRef, useEffect } from 'react';
import {
  X, Download, Share2, Copy, ExternalLink, ZoomIn, ZoomOut,
  RotateCw, Play, Pause, Volume2, VolumeX, Maximize, Minimize,
  ChevronLeft, ChevronRight, Info, Heart, MessageCircle,
  Instagram, Twitter, Facebook, Linkedin, Youtube, Settings,
  Crop, Filter, Palette, Type, Image as ImageIcon, Video
} from 'lucide-react';

function MediaPreview({
  media,
  isOpen,
  onClose,
  showSocialFormats = true,
  showControls = true,
  showInfo = true,
  allowDownload = true,
  allowShare = true,
  className = ''
}) {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [zoom, setZoom] = useState(1);
  const [rotation, setRotation] = useState(0);
  const [isPlaying, setIsPlaying] = useState(false);
  const [isMuted, setIsMuted] = useState(false);
  const [volume, setVolume] = useState(1);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [selectedFormat, setSelectedFormat] = useState('original');
  const [showMetadata, setShowMetadata] = useState(false);
  const [shareMenuOpen, setShareMenuOpen] = useState(false);

  const videoRef = useRef(null);
  const containerRef = useRef(null);

  const mediaArray = Array.isArray(media) ? media : media ? [media] : [];
  const currentMedia = mediaArray[currentIndex];
  const isVideo =
    currentMedia?.type === 'video' ||
    currentMedia?.resource_type === 'video' ||
    (currentMedia?.format && ['mp4', 'mov', 'webm'].includes(currentMedia.format.toLowerCase()));
  const isMultiple = mediaArray.length > 1;

  const socialFormats = {
    original: { name: 'Original', platform: null, aspectRatio: 'As-is' },
    instagram_square: { name: 'Instagram (1:1)', platform: 'instagram', icon: Instagram, aspectRatio: '1:1' },
    instagram_portrait: { name: 'Instagram (4:5)', platform: 'instagram', icon: Instagram, aspectRatio: '4:5' },
    instagram_landscape: { name: 'Instagram (1.91:1)', platform: 'instagram', icon: Instagram, aspectRatio: '1.91:1' },
    twitter: { name: 'Twitter (16:9)', platform: 'twitter', icon: Twitter, aspectRatio: '16:9' },
    facebook: { name: 'Facebook (1.91:1)', platform: 'facebook', icon: Facebook, aspectRatio: '1.91:1' },
    linkedin: { name: 'LinkedIn (1.91:1)', platform: 'linkedin', icon: Linkedin, aspectRatio: '1.91:1' },
    tiktok: { name: 'TikTok (9:16)', platform: 'tiktok', icon: Video, aspectRatio: '9:16' },
    youtube: { name: 'YouTube (16:9)', platform: 'youtube', icon: Youtube, aspectRatio: '16:9' },
  };

  // ✅ FIX: Avoid invalid Cloudinary transformations for VIDEO (prevents 400).
  // For videos, return a safe transformation-free Cloudinary delivery URL when possible.
  const getMediaUrl = () => {
    if (!currentMedia) return null;

    // ✅ Video: DO NOT apply image-style transforms (c_fill, g_auto:subject, so_auto, etc.)
    if (isVideo) {
      // If you already store a direct playable URL (e.g., secure_url), use it.
      if (currentMedia.url) return currentMedia.url;

      // Otherwise build a direct Cloudinary delivery URL from publicId
      const cloudName =
        process.env.REACT_APP_CLOUDINARY_CLOUD_NAME ||
        process.env.REACT_APP_CLOUDINARY_NAME ||
        'dpifdmjvv'; // fallback matches your console log

      const publicId = currentMedia.publicId || currentMedia.public_id;
      if (!publicId) return null;

      // Prefer .mp4 for consistent playback (Cloudinary will serve if available)
      return `https://res.cloudinary.com/${cloudName}/video/upload/${publicId}.mp4`;
    }

    // Images: keep your existing behavior unchanged
    if (selectedFormat === 'original') return currentMedia.url;
    const format = socialFormats[selectedFormat];
    if (format?.platform) {
      return `https://res.cloudinary.com/your_cloud_name/image/upload/w_1080,h_1080,c_fill,q_auto,f_auto/${currentMedia.publicId}`;
    }
    return currentMedia.url;
  };

  useEffect(() => {
    const handleKeyPress = (e) => {
      if (!isOpen) return;
      switch (e.key) {
        case 'Escape': onClose(); break;
        case 'ArrowLeft': if (isMultiple && currentIndex > 0) setCurrentIndex(currentIndex - 1); break;
        case 'ArrowRight': if (isMultiple && currentIndex < mediaArray.length - 1) setCurrentIndex(currentIndex + 1); break;
        case ' ': if (isVideo) { e.preventDefault(); togglePlay(); } break;
        case '+': case '=': setZoom(prev => Math.min(prev + 0.25, 3)); break;
        case '-': setZoom(prev => Math.max(prev - 0.25, 0.25)); break;
        case 'r': setRotation(prev => (prev + 90) % 360); break;
        default: break;
      }
    };
    document.addEventListener('keydown', handleKeyPress);
    return () => document.removeEventListener('keydown', handleKeyPress);
  }, [isOpen, currentIndex, mediaArray.length, isVideo, onClose]);

  useEffect(() => {
    const video = videoRef.current;
    if (!video || !isVideo) return;
    const updateTime = () => setCurrentTime(video.currentTime);
    const updateDuration = () => setDuration(video.duration);
    const handlePlay = () => setIsPlaying(true);
    const handlePause = () => setIsPlaying(false);
    const handleEnded = () => setIsPlaying(false);
    video.addEventListener('timeupdate', updateTime);
    video.addEventListener('loadedmetadata', updateDuration);
    video.addEventListener('play', handlePlay);
    video.addEventListener('pause', handlePause);
    video.addEventListener('ended', handleEnded);
    return () => {
      video.removeEventListener('timeupdate', updateTime);
      video.removeEventListener('loadedmetadata', updateDuration);
      video.removeEventListener('play', handlePlay);
      video.removeEventListener('pause', handlePause);
      video.removeEventListener('ended', handleEnded);
    };
  }, [currentMedia, isVideo]);

  const togglePlay = () => {
    if (!videoRef.current) return;
    isPlaying ? videoRef.current.pause() : videoRef.current.play();
  };

  const toggleMute = () => {
    if (!videoRef.current) return;
    videoRef.current.muted = !isMuted;
    setIsMuted(!isMuted);
  };

  const handleVolumeChange = (e) => {
    const newVolume = parseFloat(e.target.value);
    if (!videoRef.current) return;
    videoRef.current.volume = newVolume;
    setVolume(newVolume);
    if (newVolume === 0) {
      setIsMuted(true);
      videoRef.current.muted = true;
    } else if (isMuted) {
      setIsMuted(false);
      videoRef.current.muted = false;
    }
  };

  const handleSeek = (e) => {
    const newTime = parseFloat(e.target.value);
    if (!videoRef.current) return;
    videoRef.current.currentTime = newTime;
    setCurrentTime(newTime);
  };

  const toggleFullscreen = () => {
    if (!containerRef.current) return;
    if (!document.fullscreenElement) {
      containerRef.current.requestFullscreen?.();
      setIsFullscreen(true);
    } else {
      document.exitFullscreen?.();
      setIsFullscreen(false);
    }
  };

  const handleDownload = () => {
    if (!currentMedia) return;
    const link = document.createElement('a');
    link.href = getMediaUrl();
    link.download = currentMedia.originalName || `media.${currentMedia.format}`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const copyToClipboard = async (url) => {
    const tempInput = document.createElement('textarea');
    tempInput.value = url;
    document.body.appendChild(tempInput);
    tempInput.select();
    document.execCommand('copy');
    document.body.removeChild(tempInput);
  };

  const shareToSocial = (platformKey) => {
    if (!currentMedia) return;
    const url = getMediaUrl();
    const text = `Check out this ${currentMedia.type || 'media'} from Infleuince!`;
    const shareUrls = {
      twitter: `https://twitter.com/intent/tweet?text=${encodeURIComponent(text)}&url=${encodeURIComponent(url)}`,
      facebook: `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(url)}`,
      linkedin: `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(url)}`,
      email: `mailto:?subject=${encodeURIComponent(text)}&body=${encodeURIComponent(`${text}\n\n${url}`)}`
    };
    if (shareUrls[platformKey]) {
      window.open(shareUrls[platformKey], '_blank', 'width=600,height=400');
    }
    setShareMenuOpen(false);
  };

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const formatFileSize = (bytes) => {
    if (!bytes) return 'Unknown';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(1)) + ' ' + sizes[i];
  };

  if (!isOpen || !currentMedia) return null;

  return (
    <div className={`fixed inset-0 bg-black/80 backdrop-blur-2xl z-50 flex items-center justify-center animate-fade-in ${className}`}>
      <div ref={containerRef} className="relative w-full h-full flex flex-col animate-scale-in">

        {/* ✅ Glassmorphic Header */}
        {showControls && (
          <div className="absolute top-0 left-0 right-0 z-20 bg-gradient-to-b from-black/60 via-black/40 to-transparent p-6">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-4 text-white">
                {isVideo ? <Video className="w-6 h-6 text-purple-400" /> : <ImageIcon className="w-6 h-6 text-blue-400" />}
                <div>
                  <p className="text-lg font-bold">{currentMedia.originalName}</p>
                  <p className="text-sm text-gray-300">
                    {currentIndex + 1} of {mediaArray.length} • {formatFileSize(currentMedia.bytes)}
                  </p>
                </div>
              </div>

              <div className="flex items-center space-x-3">
                {showInfo && (
                  <button onClick={() => setShowMetadata(!showMetadata)} className="p-3 rounded-full bg-white/10 hover:bg-white/20 text-white transition-all">
                    <Info className="w-5 h-5" />
                  </button>
                )}

                {allowShare && (
                  <div className="relative">
                    <button onClick={() => setShareMenuOpen(!shareMenuOpen)} className="p-3 rounded-full bg-white/10 hover:bg-white/20 text-white transition-all">
                      <Share2 className="w-5 h-5" />
                    </button>
                    {shareMenuOpen && (
                      <div className="absolute top-full right-0 mt-3 w-56 bg-gray-900/90 backdrop-blur-xl rounded-2xl shadow-glow-lg border border-white/10 py-2 z-30">
                        <button onClick={() => copyToClipboard(getMediaUrl())} className="w-full flex items-center px-4 py-2 text-sm text-gray-300 hover:bg-white/10 rounded-t-2xl transition-colors">
                          <Copy className="w-4 h-4 mr-3 text-purple-400" /> Copy Link
                        </button>
                        <button onClick={() => shareToSocial('twitter')} className="w-full flex items-center px-4 py-2 text-sm text-gray-300 hover:bg-white/10 transition-colors">
                          <Twitter className="w-4 h-4 mr-3 text-blue-400" /> Share to Twitter
                        </button>
                        <button onClick={() => shareToSocial('facebook')} className="w-full flex items-center px-4 py-2 text-sm text-gray-300 hover:bg-white/10 transition-colors">
                          <Facebook className="w-4 h-4 mr-3 text-blue-600" /> Share to Facebook
                        </button>
                        <button onClick={() => shareToSocial('linkedin')} className="w-full flex items-center px-4 py-2 text-sm text-gray-300 hover:bg-white/10 transition-colors">
                          <Linkedin className="w-4 h-4 mr-3 text-blue-500" /> Share to LinkedIn
                        </button>
                        <button onClick={() => shareToSocial('email')} className="w-full flex items-center px-4 py-2 text-sm text-gray-300 hover:bg-white/10 rounded-b-2xl transition-colors">
                          <ExternalLink className="w-4 h-4 mr-3 text-green-400" /> Share via Email
                        </button>
                      </div>
                    )}
                  </div>
                )}

                {allowDownload && (
                  <button onClick={handleDownload} className="p-3 rounded-full bg-white/10 hover:bg-white/20 text-white transition-all">
                    <Download className="w-5 h-5" />
                  </button>
                )}

                <button onClick={toggleFullscreen} className="p-3 rounded-full bg-white/10 hover:bg-white/20 text-white transition-all">
                  {isFullscreen ? <Minimize className="w-5 h-5" /> : <Maximize className="w-5 h-5" />}
                </button>

                <button onClick={onClose} className="p-3 rounded-full bg-red-600/80 hover:bg-red-700/80 text-white transition-all">
                  <X className="w-5 h-5" />
                </button>
              </div>
            </div>
          </div>
        )}

        {/* ✅ Main Content Area */}
        <div className="flex-1 flex items-center justify-center relative p-6">
          {isMultiple && (
            <>
              <button
                onClick={() => { setCurrentIndex(Math.max(0, currentIndex - 1)); setSelectedFormat('original'); }}
                disabled={currentIndex === 0}
                className="absolute left-6 z-10 p-4 rounded-full bg-white/10 hover:bg-white/20 text-white disabled:opacity-30 disabled:cursor-not-allowed transition-all"
              >
                <ChevronLeft className="w-6 h-6" />
              </button>
              <button
                onClick={() => { setCurrentIndex(Math.min(mediaArray.length - 1, currentIndex + 1)); setSelectedFormat('original'); }}
                disabled={currentIndex === mediaArray.length - 1}
                className="absolute right-6 z-10 p-4 rounded-full bg-white/10 hover:bg-white/20 text-white disabled:opacity-30 disabled:cursor-not-allowed transition-all"
              >
                <ChevronRight className="w-6 h-6" />
              </button>
            </>
          )}

          <div className="relative max-w-full max-h-full">
            {isVideo ? (
              <div className="relative">
                <video
                  ref={videoRef}
                  src={getMediaUrl()}
                  className="max-w-full max-h-full object-contain cursor-pointer rounded-2xl shadow-2xl"
                  style={{ transform: `scale(${zoom}) rotate(${rotation}deg)`, transition: 'transform 0.2s ease' }}
                  onClick={togglePlay}
                  poster={currentMedia.thumbnail || currentMedia.url?.replace(/\.[^/.]+$/, '.jpg')}
                  preload="metadata"
                >
                  <source src={getMediaUrl()} type="video/mp4" />
                  <source src={getMediaUrl()} type={`video/${currentMedia.format}`} />
                  Your browser does not support the video tag.
                </video>
                {!isPlaying && (
                  <button
                    onClick={(e) => { e.stopPropagation(); togglePlay(); }}
                    className="absolute inset-0 flex items-center justify-center bg-black/40 hover:bg-black/50 transition-colors cursor-pointer"
                  >
                    <div className="bg-white/90 rounded-full p-5 transform hover:scale-110 transition-transform shadow-xl">
                      <Play className="w-10 h-10 text-purple-600" fill="currentColor" />
                    </div>
                  </button>
                )}
              </div>
            ) : (
              <img
                src={getMediaUrl()}
                alt={currentMedia.originalName}
                className="max-w-full max-h-full object-contain cursor-pointer rounded-2xl shadow-2xl"
                style={{ transform: `scale(${zoom}) rotate(${rotation}deg)`, transition: 'transform 0.2s ease' }}
                onClick={() => setZoom(zoom === 1 ? 2 : 1)}
              />
            )}
          </div>
        </div>

        {/* ✅ Social Format Pills (Glowing) */}
        {showSocialFormats && !isVideo && (
          <div className="absolute bottom-24 left-1/2 -translate-x-1/2 z-10">
            <div className="flex items-center gap-3 px-4 py-2 bg-black/60 backdrop-blur-xl rounded-full border border-white/10 shadow-glow-md">
              {Object.entries(socialFormats).map(([key, format]) => {
                const IconComponent = format.icon || ImageIcon;
                return (
                  <button
                    key={key}
                    onClick={() => setSelectedFormat(key)}
                    className={`flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium transition-all ${
                      selectedFormat === key
                        ? 'bg-gradient-to-r from-purple-600 to-blue-600 text-white shadow-glow-purple'
                        : 'text-gray-300 hover:bg-white/10 hover:text-white'
                    }`}
                  >
                    {IconComponent && <IconComponent className="w-4 h-4" />}
                    {format.name}
                  </button>
                );
              })}
            </div>
          </div>
        )}

        {/* ✅ Bottom Controls (Glass Toolbar) */}
        {showControls && (
          <div className="absolute bottom-0 left-0 right-0 z-10 bg-gradient-to-t from-black/80 via-black/40 to-transparent p-6">
            {isVideo && (
              <div className="mb-5">
                <div className="mb-3">
                  <input
                    type="range"
                    min="0"
                    max={duration || 0}
                    value={currentTime}
                    onChange={handleSeek}
                    className="w-full h-2 bg-gray-600 rounded-full appearance-none cursor-pointer [&::-webkit-slider-thumb]:bg-purple-400 [&::-moz-range-thumb]:bg-purple-400"
                  />
                  <div className="flex justify-between text-xs text-white mt-2">
                    <span>{formatTime(currentTime)}</span>
                    <span>{formatTime(duration)}</span>
                  </div>
                </div>
                <div className="flex items-center justify-center space-x-4">
                  <button onClick={togglePlay} className="p-3 rounded-full bg-white/10 hover:bg-white/20 text-white transition-all">
                    {isPlaying ? <Pause className="w-6 h-6" /> : <Play className="w-6 h-6" />}
                  </button>
                  <button onClick={toggleMute} className="p-3 rounded-full bg-white/10 hover:bg-white/20 text-white transition-all">
                    {isMuted ? <VolumeX className="w-6 h-6" /> : <Volume2 className="w-6 h-6" />}
                  </button>
                  <input
                    type="range"
                    min="0"
                    max="1"
                    step="0.1"
                    value={volume}
                    onChange={handleVolumeChange}
                    className="w-24 h-2 bg-gray-600 rounded-full appearance-none cursor-pointer [&::-webkit-slider-thumb]:bg-purple-400 [&::-moz-range-thumb]:bg-purple-400"
                  />
                </div>
              </div>
            )}

            {!isVideo && (
              <div className="flex items-center justify-center space-x-4">
                <button onClick={() => setZoom(Math.max(0.25, zoom - 0.25))} disabled={zoom <= 0.25} className="p-3 rounded-full bg-white/10 hover:bg-white/20 text-white disabled:opacity-30 transition-all">
                  <ZoomOut className="w-6 h-6" />
                </button>
                <span className="text-white text-sm font-medium min-w-[70px] text-center">{Math.round(zoom * 100)}%</span>
                <button onClick={() => setZoom(Math.min(3, zoom + 0.25))} disabled={zoom >= 3} className="p-3 rounded-full bg-white/10 hover:bg-white/20 text-white disabled:opacity-30 transition-all">
                  <ZoomIn className="w-6 h-6" />
                </button>
                <button onClick={() => setRotation((rotation + 90) % 360)} className="p-3 rounded-full bg-white/10 hover:bg-white/20 text-white transition-all">
                  <RotateCw className="w-6 h-6" />
                </button>
                <button onClick={() => { setZoom(1); setRotation(0); }} className="px-4 py-2 rounded-xl bg-white/10 hover:bg-white/20 text-white text-sm transition-all">
                  Reset
                </button>
              </div>
            )}
          </div>
        )}

        {/* ✅ Metadata Panel (Glass) */}
        {showMetadata && showInfo && (
          <div className="absolute top-24 right-6 w-80 bg-gray-900/80 backdrop-blur-2xl rounded-2xl shadow-glow-lg border border-white/10 p-5 z-20 animate-slide-left">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-bold text-white">Media Info</h3>
              <button onClick={() => setShowMetadata(false)} className="text-gray-400 hover:text-white transition-colors">
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="space-y-3 text-sm text-gray-300">
              <div className="flex justify-between"><span className="text-gray-400">File:</span><span className="font-medium truncate">{currentMedia.originalName}</span></div>
              <div className="flex justify-between"><span className="text-gray-400">Type:</span><span>{currentMedia.type}</span></div>
              <div className="flex justify-between"><span className="text-gray-400">Format:</span><span className="uppercase">{currentMedia.format}</span></div>
              <div className="flex justify-between"><span className="text-gray-400">Size:</span><span>{formatFileSize(currentMedia.bytes)}</span></div>
              <div className="flex justify-between"><span className="text-gray-400">Dimensions:</span><span>{currentMedia.width} × {currentMedia.height}</span></div>
              {currentMedia.duration && <div className="flex justify-between"><span className="text-gray-400">Duration:</span><span>{formatTime(currentMedia.duration)}</span></div>}
              <div className="flex justify-between"><span className="text-gray-400">Uploaded:</span><span>{new Date(currentMedia.createdAt).toLocaleDateString()}</span></div>
              {currentMedia.tags?.length > 0 && (
                <div>
                  <span className="text-gray-400">Tags:</span>
                  <div className="flex flex-wrap gap-2 mt-2">
                    {currentMedia.tags.map(tag => (
                      <span key={tag} className="bg-purple-500/20 text-purple-300 px-2 py-1 rounded text-xs border border-purple-500/30">
                        {tag}
                      </span>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default MediaPreview;
